import axios, { AxiosError, AxiosResponse } from 'axios';
import type { ManifestEntry } from './manifestDiff';
import type { AssembledTargetedFetch } from './manifestFullCache';

// Tipi e interfacce
export interface ApiResponse<T> {
  data: T;
  error?: string;
  status: number;
}

// Nomi proprietà = nomi campo FM reali (vedi server/src/services/uscitaAssembler.ts), non alias
// legacy del vecchio JSON web_usc_pwa. Le uniche eccezioni sono valori genuinamente calcolati da
// noi (non un singolo campo): conversioni ora→minuti (suffisso _min, dove serve aritmetica/confronti
// numerici) e gruppo_cg_nome/gruppo_cg_id (scelta della prima riga capogruppo).
export interface Ragazzo {
  uscprod_ck_1_no_pausa: number;
  mob_dip_id: number;
  uscprod_ora_inizio?: string;
  uscprod_ora_fine?: string;
  mob_dip_nome: string;
  mob_usc_id: number;
  mob_uscprod_id: number;
  recordId: number;
  // modId nativo FM — usato per costruire/confrontare il manifest (manifestDiff.ts) senza una
  // chiamata /manifest separata dopo get-flat.
  modId?: number;
}

export interface Volantino {
  // recordId FM — usato per chiavare la cache manifest (manifestFullCache.ts).
  recordId?: number;
  modId?: number;
  mob_usclocloc_id_unico: string;
  mob_usclocloc_id: number;
  mob_usc_id: number;
  mob_usclocloc_ck_1_show_qta: number;
  mob_usclocloc_carico_vol: number;
  mob_carico_vol_nome: string;
  vol_nome_produzione?: string;
  mob_off_pdv_id: number;
  mob_pdv_nome: string;
  mob_data_promo: string;
  mob_canale_id: string;
  mob_carico_ins: number;
  mob_carico_tecnologia: string;
  mob_carico_ins_pacco: number | string;
  mob_carico_vol_pacco: number | string;
  mob_carico_ins_n_pacchi: number | string;
  mob_carico_vol_n_pacchi: number | string;
  mob_carico_cassette_multiple: string;
  mob_carico_cond_piu_vol: number;
  mob_carico_cond_perc: number;
  mob_carico_cond_max_vol: number;
  mob_carico_copertura: string;
  mob_usclocloc_posiz_vol: number;
  mob_usclocloc_carico_ins: number;
  mob_mc_vol_messi: number;
  mob_ck_1_carico: number;
  mob_usclocloc_ck_1_foto: number;
  mob_usclocloc_ck_1_ometti: number;
  usc_usclocloc_ck_1_uso_tecnologia: number;
  mob_usclocloc_note: string;
}

export interface Rifornimento {
  mob_auto_targa: string;
  mob_rifornimento_costo_litro: number;
  mob_rifornimento_costo_tot: number;
  mob_rifornimento_id: number;
  mob_usc_id: number;
  recordId: number;
  modId?: number;
}

export interface Carico {
  mob_carico_id: number;
  mob_usc_id: number;
  mob_off_pdv_id: number;
  mob_carico_ora: string;
  mob_carico_n_pedana_ins: number | string;
  mob_carico_n_pedana_vol: number | string;
  mob_carico_sfusi_ins: number;
  mob_carico_sfusi_vol: number;
  mob_carico_pacchi_ins: number;
  mob_carico_pacchi_vol: number;
  mob_scarico_sfusi_ins: number;
  mob_scarico_sfusi_vol: number;
  mob_scarico_pacchi_ins: number;
  mob_scarico_pacchi_vol: number;
  recordId: number;
  modId?: number;
}

export interface LocalitaFeature {
  geometry?: {
    coordinates: number[][][][];
    type: "MultiPolygon";
  };
  id: string;
  // recordId FM della riga uscloc — usato per chiavare la cache manifest (manifestFullCache.ts),
  // non per identificare la zona (quello resta "id" sopra, mob_id_unico).
  recordId?: number;
  modId?: number;
  type: "Feature";
  properties: {
    mob_id_unico: string;
    mob_uscloc_id: number;
    ora_apertura_latlon: string;
    ora_chiusura_latlon: string;
    loc_ck_chiusa: number;
    mob_comune: string;
    ora_apertura_min: number;
    ora_chiusura_min: number;
    mob_ora_in_prev: string;
    mob_ora_out_prev: string;
    pausa_tot_ore: string;
    mob_localita: string;
    mob_uscloc_order: number;
    mob_localita_latlon: string;
    mob_usc_data: string;
    mob_usc_id: number;
    mob_via_partenza: string;
    mob_via_partenza_latlon: string;
    mob_vol_num: number;
    volantini: Volantino[];
  };
}

export interface Uscita {
  mob_usc_id: number;
  // recordId FM dell'uscita — usato per scrivere via GET-by-recordId+PATCH invece di una ricerca.
  recordId: number;
  // modId nativo FM — usato per costruire/confrontare il manifest (manifestDiff.ts) senza una
  // chiamata /manifest separata dopo get-flat.
  modId?: number;
  mob_soc_id: number;
  todo: any[];
  mob_usc_data: string;
  mob_usc_num: number;
  gruppo_cg_nome: string;
  gruppo_cg_id: number;
  gruppo_rag_num: number;
  mob_mag_id: number;
  mob_mag_nome: string;
  mob_mag_latlon: string;
  // Secondi tra un refresh e l'altro del manifest — campo correlato da MOB_USER (Società), stesso
  // valore su ogni uscita della stessa azienda. Vedi manifestDiff.ts::extractManifestFromUscite.
  mob_user_update_timesec?: number;
  mob_usc_auto_targa: string;
  mob_usc_note_x_cg: string;
  mob_usc_1_fornitore: number;
  mob_ck_1_show_orari_prev: number;
  rifornimento?: Rifornimento[]; // Array rifornimenti
  carico?: Carico[]; // Array eventi carico/scarico mezzo
  ragazzi: Ragazzo[]; // già include la possibilità di array vuoto
  capogruppo: Ragazzo[]; // Campo per i dati del capogruppo, array sempre presente (può essere vuoto)
  localita: LocalitaFeature[]; // già include la possibilità di array vuoto

  /*########################*/
  /* CAMPI PER UPDATE USCITE*/
  usc_ora_apertura: string;
  usc_ora_chiusura: string;
  usc_ora_inizio_pausa: string;
  usc_ora_fine_pausa: string;
  usc_pausa_tot_ore: string;
  usc_auto_km_totali_reali: number;
  usc_ora_apertura_latlon: string;
  usc_ora_chiusura_latlon: string;
  usc_note_da_cg: string;
  /*########################*/

}

export interface UsciteResponse {
  uscite: Uscita[];
}

// Formatta la data nel formato richiesto dal server (mm-dd-yyyy)
const formatDateForServer = (date: Date): string => {
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

// Configurazione di base
// Se REACT_APP_API_URL è definita (produzione Docker) la usa direttamente,
// altrimenti costruisce l'URL dal hostname corrente e dalla porta (sviluppo locale)
const currentHost = window.location.hostname;
const envApiPort = (process.env.REACT_APP_API_PORT || '').trim();
const apiPort = envApiPort !== '' ? envApiPort : '3001';
const apiBaseUrl = process.env.REACT_APP_API_URL || `http://${currentHost}:${apiPort}`;

console.log('[API] Configurazione con base URL:', apiBaseUrl);

// Debug mode
const DEBUG = true;

// Configura axios con le opzioni essenziali
const apiClient = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true  // Manteniamo questo per i cookie
});

// Interceptor essenziale per catturare gli errori di rete
apiClient.interceptors.request.use(
  (config: any) => {
    if (DEBUG) {
      console.log('[API] Richiesta in uscita:', {
        url: config.url,
        method: config.method,
        headers: config.headers,
        data: config.data
      });
    }
    return config;
  },
  (error: any) => {
    console.error('[API] Errore nella preparazione della richiesta:', error);
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (DEBUG) {
      console.log('[API] Risposta ricevuta:', {
        status: response.status,
        headers: response.headers,
        data: response.data
      });
    }
    return response;
  },
  async (error: AxiosError) => {
    console.error('[API] Errore di rete:', error);
    // Verifica se l'errore è un problema di CORS
    if (error.message && error.message.includes('Network Error')) {
      console.error('[API] Possibile problema di CORS o server non raggiungibile');
      console.error('[API] URL richiesta:', error.config?.url);
      console.error('[API] Metodo richiesta:', error.config?.method);
      console.error('[API] Headers:', error.config?.headers);
      
      // Verifichiamo se il server è raggiungibile
      checkServerStatus();
    }
    
    // Gestione automatica del token scaduto
    const originalRequest = error.config as any;
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Se la richiesta fallita non è già /api/login/refresh
      if (!originalRequest.url?.includes('/api/login/refresh')) {
        try {
          console.log('[API] Token scaduto, tentativo di rinnovo automatico...');
          // Tenta di rinnovare il token
          const refreshResponse = await authService.refreshToken();
          
          if (refreshResponse.data?.session_token) {
            console.log('[API] Token rinnovato con successo, ripetizione della richiesta originale');
            // Aggiorna l'header della richiesta originale con il nuovo token
            if (originalRequest.headers) {
              originalRequest.headers['X-Session-Token'] = sessionStorage.getItem('sessionToken') || '';
            }
            
            // Ripeti la richiesta originale
            return apiClient(originalRequest);
          }
        } catch (refreshError) {
          console.error('[API] Impossibile rinnovare il token:', refreshError);
          // Sessione scaduta, reindirizza al login
          sessionStorage.removeItem('sessionToken');
          sessionStorage.removeItem('userDetails');
          window.location.href = '/login';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Funzione per verificare lo stato del server
async function checkServerStatus() {
  try {
    console.log('[API] Verifica stato del server...');
    const response = await fetch(`${apiBaseUrl}/api/status`, {
      method: 'GET',
      mode: 'no-cors' // Proviamo senza CORS per verificare che il server sia almeno online
    });
    console.log('[API] Server raggiungibile, stato risposta:', response.status);
  } catch (error) {
    console.error('[API] Server non raggiungibile:', error);
  }
}

// NUOVA FUNZIONE PER GESTIRE GLI ERRORI DI TOKEN
export const handleApiTokenError = (error: any): string => {
  console.log('[API] Analisi errore per token:', error);
  
  // Errore specifico con codice TOKEN_EXPIRED
  if (error?.response?.data?.code === 'TOKEN_EXPIRED') {
    return 'La tua sessione è scaduta. Effettua nuovamente l\'accesso.';
  }
  
  // Errore FileMaker token scaduto
  if (error?.response?.data?.error === 'Sessione FileMaker scaduta') {
    return 'Sessione scaduta. Riaccedi per continuare.';
  }
  
  // Errore generico 401
  if (error?.response?.status === 401) {
    return 'Accesso non autorizzato. Effettua nuovamente il login.';
  }
  
  // Errore di connessione/rete
  if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network Error')) {
    return 'Problemi di connessione. Verifica la connessione internet.';
  }
  
  // Token renewal fallito
  if (error?.message === 'TOKEN_RENEWAL_FAILED') {
    return 'Impossibile rinnovare la sessione. Riaccedi per continuare.';
  }
  
  // Messaggi che contengono riferimenti al token
  if (error?.message && (
    error.message.includes('token') || 
    error.message.includes('Token') ||
    error.message.includes('session') ||
    error.message.includes('Session') ||
    error.message.includes('Unauthorized')
  )) {
    return 'Sessione non valida. Effettua nuovamente l\'accesso.';
  }
  
  // Ritorna messaggio originale se non è un errore di token riconosciuto
  return error?.response?.data?.message || error?.message || 'Errore sconosciuto';
};

// Servizio di autenticazione semplificato
export const authService = {
  // Inizializza la sessione prima del login
  async initSession(): Promise<ApiResponse<any>> {
    try {
      console.log('[API] Inizializzazione sessione...');
      const response = await apiClient.get('/api/status');
      console.log('[API] Sessione inizializzata:', response.data);
      return {
        data: response.data,
        status: response.status
      };
    } catch (error) {
      console.error('[API] Errore nell\'inizializzazione della sessione:', error);
      const axiosError = error as AxiosError;
      return {
        data: null,
        error: `Errore di inizializzazione sessione: ${axiosError.message || 'Errore sconosciuto'}`,
        status: axiosError.response?.status || 500
      };
    }
  },

  async login(username: string, password: string): Promise<ApiResponse<any>> {
    try {
      // Prima inizializza la sessione
      // console.log('[API] Tentativo di initSession da login...');
      // await this.initSession(); // Temporaneamente commentato per debug
      console.log(`[API] Tentativo di login per utente: ${username}`);
      
      // Verifichiamo che il server sia disponibile
      try {
        console.log(`[API] Verifica disponibilità server prima di /api/login. URL base: ${apiClient.defaults.baseURL}`);
        const testResponse = await apiClient.get('/api/status');
        console.log('[API] Server disponibile per /api/status:', testResponse.data);
      } catch (error) {
        console.error('[API] Server non disponibile durante il check /api/status:', error);
        // Decidi se vuoi propagare questo errore o gestirlo diversamente
        // throw new Error('Server non raggiungibile. Riprovare più tardi.'); 
      }
      
      // Log prima della chiamata POST effettiva
      console.log(`[API] Chiamata POST a: ${apiClient.defaults.baseURL}/api/auth/login`);
      console.log(`[API] Payload per /api/auth/login:`, { username, password });

      // Effettua il login utilizzando il nuovo endpoint
      const response = await apiClient.post('/api/auth/login', {
        username,
        password
      });
      
      console.log('[API] Risposta da /api/auth/login:', response.data);
      
      if (response.data.user_data) {
        // Salva i dati utente in sessionStorage
        sessionStorage.setItem('userDetails', JSON.stringify(response.data.user_data));
        
        // Salva anche il token di sessione se presente
        if (response.data.session_token) {
          console.log('[API] Token di sessione ricevuto, salvataggio...');
          sessionStorage.setItem('sessionToken', response.data.session_token);
        }
      }
      
      return {
        data: response.data,
        status: response.status
      };
    } catch (error) {
      console.error('[API] Errore nel processo di login:', error);
      const axiosError = error as AxiosError;
      
      // Log dettagliato dell'errore
      if (axiosError.response) {
        console.error('[API] Risposta di errore dal server:', {
          status: axiosError.response.status,
          data: axiosError.response.data
        });
      } else if (axiosError.request) {
        console.error('[API] Nessuna risposta dal server');
      } else {
        console.error('[API] Errore di configurazione:', axiosError.message);
      }
      
      return {
        data: null,
        error: `Errore di autenticazione: ${axiosError.message || 'Errore sconosciuto'}`,
        status: axiosError.response?.status || 500
      };
    }
  },
  
  isLoggedIn(): boolean {
    return sessionStorage.getItem('userDetails') !== null && 
           sessionStorage.getItem('sessionToken') !== null;
  },
  
  // Chiama il backend per chiudere davvero la sessione FileMaker prima di pulire lo stato
  // locale — l'interceptor su apiClient allega ancora X-Session-Token qui perche lo rimuoviamo
  // da sessionStorage solo dopo. Se la chiamata fallisce (rete assente, server giu) non deve
  // comunque impedire il logout locale: l'utente vuole uscire subito, la sessione FM residua
  // scadra da sola sul server.
  async logout(): Promise<void> {
    try {
      await apiClient.post('/api/auth/logout');
    } catch (error) {
      console.warn('[API] Logout lato server fallito, procedo comunque con la pulizia locale:', error);
    } finally {
      sessionStorage.removeItem('userDetails');
      sessionStorage.removeItem('sessionToken');
    }
  },
  
  async qrLogin(recordId: number): Promise<ApiResponse<any>> {
    try {
      console.log(`[API] QR login per recordId: ${recordId}`);
      const response = await apiClient.post('/api/auth/qr-login', { record_id: recordId });

      if (response.data.user_data) {
        sessionStorage.setItem('userDetails', JSON.stringify(response.data.user_data));
      }
      if (response.data.session_token) {
        sessionStorage.setItem('sessionToken', response.data.session_token);
      }

      return { data: response.data, status: response.status };
    } catch (error) {
      console.error('[API] Errore QR login:', error);
      const axiosError = error as AxiosError;
      return {
        data: null,
        error: `Errore QR login: ${axiosError.message || 'Errore sconosciuto'}`,
        status: axiosError.response?.status || 500,
      };
    }
  },

  async refreshToken(): Promise<ApiResponse<any>> {
    try {
      const sessionToken = sessionStorage.getItem('sessionToken');
      if (!sessionToken) {
        return {
          data: null,
          error: 'Nessun token da rinnovare',
          status: 400
        };
      }
      
      console.log('[API] Tentativo di rinnovo token...');
      const response = await apiClient.post('/api/refresh', {
        session_token: sessionToken
      });
      
      if (response.data.session_token) {
        console.log('[API] Nuovo token ricevuto, aggiornamento...');
        sessionStorage.setItem('sessionToken', response.data.session_token);
      }
      
      return {
        data: response.data,
        status: response.status
      };
    } catch (error) {
      console.error('[API] Errore nel rinnovo del token:', error);
      const axiosError = error as AxiosError;
      
      return {
        data: null,
        error: `Errore di rinnovo token: ${axiosError.message || 'Errore sconosciuto'}`,
        status: axiosError.response?.status || 500
      };
    }
  }
};

// Aggiungiamo un interceptor semplificato per il token di sessione
apiClient.interceptors.request.use((config: any) => {
  const sessionToken = sessionStorage.getItem('sessionToken');
  if (sessionToken && config.headers) {
    // Aggiungi solo l'header
    config.headers['X-Session-Token'] = sessionToken;
  }
  return config;
});

// Funzione per ottenere le uscite
export async function getUscite(dataIn: Date, dataOut: Date): Promise<ApiResponse<UsciteResponse>> {
  try {
    // Formatta le date nel formato richiesto dall'API
    const dataInFormatted = formatDateForServer(dataIn);
    const dataOutFormatted = formatDateForServer(dataOut);
    
    console.log(`[API] Richiesta uscite dal ${dataInFormatted} al ${dataOutFormatted}`);
    
    // URL relativo, verrà preposto il baseUrl
    // TEST refactor/uscita-manifest: layout piatti (pwa_uscite_new + pwa_uscprod/uscloc/usclocloc/
    // carico/rifornimento), join lato JS, nomi proprietà = campi FM reali. Nessuna geometria (vedi
    // resolveGeometriaPerUscite in Uscite.tsx). Ripristinare '/api/uscite/get' prima del merge se si
    // decide di non procedere con questo path.
    const url = '/api/uscite/get-flat';
    
    // Token di sessione per l'autenticazione
    const sessionToken = sessionStorage.getItem('sessionToken');
    
    if (!sessionToken) {
      console.error('[API] Token di sessione non trovato, autenticazione necessaria');
      return {
        data: { uscite: [] },
        error: 'Autenticazione necessaria',
        status: 401
      };
    }
    
    // Effettua la richiesta
    const response = await apiClient.post(url, {
      dataIn: dataInFormatted,
      dataOut: dataOutFormatted,
      session_token: sessionToken
    });
    
    console.log(`[API] Uscite ricevute: ${response.data.uscite?.length || 0} elementi`);
    
    return {
      data: response.data,
      status: response.status
    };
  } catch (error) {
    console.error('[API] Errore durante il recupero delle uscite:', error);
    const axiosError = error as AxiosError;
    
    // Usa la nuova funzione per gestire gli errori di token
    const friendlyError = handleApiTokenError(axiosError);
    
    // Se è un errore di token scaduto, pulisci la sessione
    if (axiosError.response?.status === 401) {
      sessionStorage.removeItem('sessionToken');
      sessionStorage.removeItem('userDetails');
    }
    
    return {
      data: { uscite: [] },
      error: friendlyError,
      status: axiosError.response?.status || 500
    };
  }
}

// Campi scrivibili dell'uscita (blocco "CAMPI DA RIEMPIRE DA WEB" su pwa_uscite) — stessa whitelist del backend
export type UscitaWritableFields = Pick<Uscita,
  | 'usc_ora_apertura'
  | 'usc_ora_chiusura'
  | 'usc_ora_inizio_pausa'
  | 'usc_ora_fine_pausa'
  | 'usc_pausa_tot_ore'
  | 'usc_auto_km_totali_reali'
  | 'usc_ora_apertura_latlon'
  | 'usc_ora_chiusura_latlon'
  | 'usc_note_da_cg'
>;

// Funzione per aggiornare i campi orari/km/note dell'uscita su FileMaker.
// recordId è il recordId FM dell'uscita (uscita.recordId) — fetch diretto lato backend, non usc_id.
// expectedModId (opzionale): il modId noto al momento in cui la scrittura è stata accodata (vedi
// writeQueue.ts) — se il record è stato modificato nel frattempo il backend risponde 409, non
// sovrascrive alla cieca. Vedi CLAUDE.md sulla policy di risoluzione conflitti.
export async function updateUscita(
  recordId: number,
  fields: Partial<UscitaWritableFields>,
  expectedModId?: number
): Promise<ApiResponse<{ ok: true }>> {
  try {
    const url = '/api/uscite/update';
    const sessionToken = sessionStorage.getItem('sessionToken');

    if (!sessionToken) {
      console.error('[API] Token di sessione non trovato, autenticazione necessaria');
      return {
        data: { ok: true },
        error: 'Autenticazione necessaria',
        status: 401
      };
    }

    const response = await apiClient.patch(url, {
      recordId,
      fields,
      expectedModId,
      session_token: sessionToken
    });

    console.log(`[API] Uscita (recordId ${recordId}) aggiornata:`, fields);

    return {
      data: response.data,
      status: response.status
    };
  } catch (error) {
    console.error(`[API] Errore durante l'aggiornamento dell'uscita (recordId ${recordId}):`, error);
    const axiosError = error as AxiosError;
    const friendlyError = handleApiTokenError(axiosError);

    if (axiosError.response?.status === 401) {
      sessionStorage.removeItem('sessionToken');
      sessionStorage.removeItem('userDetails');
    }

    return {
      data: { ok: true },
      error: friendlyError,
      status: axiosError.response?.status || 500
    };
  }
}

// Manifest leggero (recordId+modId annidati) per il pattern manifest+cache — vedi
// src/services/manifestDiff.ts e manifestCache.ts. Non ancora usato da nessun componente UI,
// solo il round-trip fetch->diff->cache è completo (pezzi 1+5+6 del piano in CLAUDE.md).
export async function fetchUsciteManifest(dataIn: Date, dataOut: Date): Promise<ApiResponse<{ manifest: ManifestEntry[] }>> {
  try {
    const dataInFormatted = formatDateForServer(dataIn);
    const dataOutFormatted = formatDateForServer(dataOut);
    const sessionToken = sessionStorage.getItem('sessionToken');

    if (!sessionToken) {
      console.error('[API] Token di sessione non trovato, autenticazione necessaria');
      return {
        data: { manifest: [] },
        error: 'Autenticazione necessaria',
        status: 401
      };
    }

    const response = await apiClient.post('/api/uscite/manifest', {
      dataIn: dataInFormatted,
      dataOut: dataOutFormatted,
      session_token: sessionToken
    });

    console.log(`[API] Manifest ricevuto: ${response.data.manifest?.length || 0} uscite`);

    return {
      data: response.data,
      status: response.status
    };
  } catch (error) {
    console.error('[API] Errore durante il recupero del manifest:', error);
    const axiosError = error as AxiosError;
    const friendlyError = handleApiTokenError(axiosError);

    if (axiosError.response?.status === 401) {
      sessionStorage.removeItem('sessionToken');
      sessionStorage.removeItem('userDetails');
    }

    return {
      data: { manifest: [] },
      error: friendlyError,
      status: axiosError.response?.status || 500
    };
  }
}

export interface TargetedFetchRecordIds {
  uscite?: number[];
  uscloc?: number[];
  usclocloc?: number[];
  uscprod?: number[];
  carico?: number[];
  rifornimento?: number[];
}

const emptyAssembledTargetedFetch: AssembledTargetedFetch = {
  uscite: [], uscloc: [], usclocloc: [], capogruppo: [], ragazzi: [], carico: [], rifornimento: []
};

// Fetch mirato (pezzo 3 del pattern manifest+cache) — dati pieni per i soli recordId segnati
// added/changed dal diff (manifestDiff.ts). dataIn/dataOut devono essere lo stesso range usato
// per il manifest: il backend li usa per ri-derivare lo scoping di carico/rifornimento (vedi
// uscite_service.ts::getUsciteTargetedFetch).
export async function fetchUsciteRecords(
  dataIn: Date,
  dataOut: Date,
  recordIds: TargetedFetchRecordIds
): Promise<ApiResponse<AssembledTargetedFetch>> {
  try {
    const dataInFormatted = formatDateForServer(dataIn);
    const dataOutFormatted = formatDateForServer(dataOut);
    const sessionToken = sessionStorage.getItem('sessionToken');

    if (!sessionToken) {
      console.error('[API] Token di sessione non trovato, autenticazione necessaria');
      return {
        data: emptyAssembledTargetedFetch,
        error: 'Autenticazione necessaria',
        status: 401
      };
    }

    const response = await apiClient.post('/api/uscite/fetch-records', {
      dataIn: dataInFormatted,
      dataOut: dataOutFormatted,
      recordIds,
      session_token: sessionToken
    });

    return {
      data: response.data,
      status: response.status
    };
  } catch (error) {
    console.error('[API] Errore durante il fetch mirato:', error);
    const axiosError = error as AxiosError;
    const friendlyError = handleApiTokenError(axiosError);

    if (axiosError.response?.status === 401) {
      sessionStorage.removeItem('sessionToken');
      sessionStorage.removeItem('userDetails');
    }

    return {
      data: emptyAssembledTargetedFetch,
      error: friendlyError,
      status: axiosError.response?.status || 500
    };
  }
}

// Aggiorniamo il servizio uscite
export const usciteService = {
  getUscite, // Ora è correttamente dichiarato
  updateUscita,
  fetchUsciteManifest,
  fetchUsciteRecords
};

// Esportazione corretta assegnando prima a una variabile
const api = {
  uscite: usciteService
};

export default api; 